import React from 'react';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';

// Living documentation of the request path & TLS model for *.home.nakomis.com
// (HOME-184). Kept deliberately simple/static — it describes the design, it
// doesn't probe it. Update this if the termination model changes.
//
// Model: re-encrypting bridge. Leia terminates the front TLS and enforces the
// client cert (mTLS), then opens a *new* TLS connection to each box, which
// terminates its own TLS with its own per-box wildcard cert. Nothing crosses
// the wire in clear — "SSL across the network".

const C = {
    node: '#2a2f37',
    edge: '#4db6ac',       // teal — matches app primary
    text: '#e0e0e0',
    sub: '#9aa0a6',
    arrow: '#9e9e9e',
    warn: '#ffb74d',
    key: '#4db6ac',
};

const ArchitectureCard: React.FC = () => (
    <Paper sx={{ p: 2 }} data-testid='architecture-card'>
        <Typography variant='h6' sx={{ mb: 0.5 }}>Request path &amp; TLS</Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Re-encrypting bridge: Leia terminates the front TLS and enforces the client
            certificate, then re-encrypts to each box, which terminates its own TLS. Every
            leg is encrypted; the client cert is required on every <code>*.home.nakomis.com</code>.
        </Typography>

        {/* Horizontal flow — scrolls on narrow screens rather than squashing */}
        <Box sx={{ overflowX: 'auto' }}>
            <Box component='svg' viewBox='0 0 900 300' sx={{ minWidth: 760, width: '100%', height: 'auto' }}>
                <defs>
                    <marker id='arrow' markerWidth='10' markerHeight='10' refX='8' refY='3'
                        orient='auto' markerUnits='strokeWidth'>
                        <path d='M0,0 L8,3 L0,6 Z' fill={C.arrow} />
                    </marker>
                </defs>

                {/* Browser */}
                <rect x='10' y='110' width='170' height='80' rx='8' fill={C.node} stroke={C.edge} />
                <text x='95' y='140' textAnchor='middle' fill={C.text} fontSize='15' fontWeight='600'>Browser</text>
                <text x='95' y='162' textAnchor='middle' fill={C.warn} fontSize='11'>🔒 presents client cert</text>
                <text x='95' y='178' textAnchor='middle' fill={C.sub} fontSize='10'>(nakotp CA, in keychain)</text>

                {/* edge: browser -> router (encrypted) */}
                <line x1='180' y1='150' x2='260' y2='150' stroke={C.arrow} strokeWidth='2' markerEnd='url(#arrow)' />
                <text x='220' y='140' textAnchor='middle' fill={C.sub} fontSize='10'>HTTPS 443</text>

                {/* Router / DNS */}
                <rect x='262' y='110' width='170' height='80' rx='8' fill={C.node} stroke={C.edge} />
                <text x='347' y='138' textAnchor='middle' fill={C.text} fontSize='14' fontWeight='600'>Router</text>
                <text x='347' y='158' textAnchor='middle' fill={C.sub} fontSize='10'>*.home.nakomis.com</text>
                <text x='347' y='172' textAnchor='middle' fill={C.sub} fontSize='10'>→ WAN IP, hairpin :443</text>

                {/* edge: router -> Leia (encrypted) */}
                <line x1='432' y1='150' x2='512' y2='150' stroke={C.arrow} strokeWidth='2' markerEnd='url(#arrow)' />

                {/* Leia (front termination + mTLS gate) */}
                <rect x='514' y='90' width='190' height='120' rx='8' fill={C.node} stroke={C.warn} strokeWidth='2' />
                <text x='609' y='116' textAnchor='middle' fill={C.text} fontSize='15' fontWeight='600'>Leia — nginx</text>
                <text x='609' y='138' textAnchor='middle' fill={C.warn} fontSize='11'>🔒 terminates front TLS</text>
                <text x='609' y='155' textAnchor='middle' fill={C.warn} fontSize='11'>verifies client cert</text>
                <text x='609' y='170' textAnchor='middle' fill={C.sub} fontSize='10'>401 if cert absent</text>
                <text x='609' y='192' textAnchor='middle' fill={C.sub} fontSize='10'>then re-encrypts →</text>

                {/* edge: Leia -> backends (re-encrypted TLS, solid + lock) */}
                <line x1='704' y1='150' x2='770' y2='150' stroke={C.edge} strokeWidth='2' markerEnd='url(#arrow)' />
                <text x='737' y='140' textAnchor='middle' fill={C.edge} fontSize='9'>🔒 TLS</text>
                <text x='737' y='176' textAnchor='middle' fill={C.sub} fontSize='9'>re-encrypt</text>

                {/* Backends — each terminates its own TLS */}
                <rect x='772' y='94' width='120' height='36' rx='6' fill={C.node} stroke={C.edge} />
                <text x='832' y='110' textAnchor='middle' fill={C.text} fontSize='12'>Luke</text>
                <text x='832' y='124' textAnchor='middle' fill={C.sub} fontSize='9'>🔒 *.luke cert</text>
                <rect x='772' y='132' width='120' height='36' rx='6' fill={C.node} stroke={C.edge} />
                <text x='832' y='148' textAnchor='middle' fill={C.text} fontSize='12'>Leia</text>
                <text x='832' y='162' textAnchor='middle' fill={C.sub} fontSize='9'>🔒 *.leia cert</text>
                <rect x='772' y='170' width='120' height='36' rx='6' fill={C.node} stroke={C.edge} />
                <text x='832' y='186' textAnchor='middle' fill={C.text} fontSize='12'>Cal</text>
                <text x='832' y='200' textAnchor='middle' fill={C.sub} fontSize='9'>🔒 *.cal cert</text>

                {/* status-endpoint note */}
                <text x='450' y='250' textAnchor='middle' fill={C.sub} fontSize='11'>
                    e.g. api.cal.home.nakomis.com/docker/status → Leia (front TLS + mTLS) → https://cal.local (own cert) → status svc
                </text>
                <text x='450' y='270' textAnchor='middle' fill={C.sub} fontSize='11'>
                    Both legs are encrypted; Leia is the only mTLS gate the browser has to satisfy.
                </text>
            </Box>
        </Box>

        {/* Cert requirements legend */}
        <Stack direction='row' spacing={2} flexWrap='wrap' useFlexGap sx={{ mt: 1 }}>
            <Box>
                <Chip size='small' label='Front server cert (Leia)' sx={{ backgroundColor: C.key, color: '#0b1f1c', mb: 0.5 }} />
                <Typography variant='caption' component='div' color='text.secondary' sx={{ maxWidth: 300 }}>
                    Let&apos;s Encrypt via certbot <code>dns-route53</code>.
                    <code>*.home.nakomis.com</code> plus <code>*.cal / *.luke / *.leia.home.nakomis.com</code> SANs
                    {' '}(so two-label names like <code>api.cal…</code> are covered).
                </Typography>
            </Box>
            <Box>
                <Chip size='small' label='Client cert (mTLS)' sx={{ backgroundColor: C.warn, color: '#3a2600', mb: 0.5 }} />
                <Typography variant='caption' component='div' color='text.secondary' sx={{ maxWidth: 300 }}>
                    Issued by the nakotp home CA; <strong>required on every <code>*.home.nakomis.com</code></strong>.
                    Leia returns 401 without it. Installed in the browser/keychain.
                </Typography>
            </Box>
            <Box>
                <Chip size='small' label='Per-box cert' sx={{ backgroundColor: C.edge, color: '#0b1f1c', mb: 0.5 }} />
                <Typography variant='caption' component='div' color='text.secondary' sx={{ maxWidth: 300 }}>
                    Each box holds its own <code>*.&lt;box&gt;.home.nakomis.com</code> cert and terminates the
                    re-encrypted back leg — SSL end-to-end across the LAN.
                </Typography>
            </Box>
        </Stack>
    </Paper>
);

export default ArchitectureCard;
